import { IDomainWideProjectRole } from "@kuika/kuika-cl-model";

/**
 * A workspace-level view of one app role, folded across every project in the domain (KOA-18634).
 * The backend (`/domains/{id}/roles`) already returns the domain's roles ordered by project
 * creation date, so folding by name here preserves the same first-seen canonical id the code
 * generator and DB convergence use — no separate server aggregation needed.
 */
export interface IWorkspaceRole {
  /** Display name — the first occurrence's spelling. */
  name: string;
  /** Distinct role ids seen under this name across projects (first-seen order; [0] is canonical). */
  roleIds: string[];
  /** Number of distinct projects this role appears in. */
  projectCount: number;
  occurrences: IDomainWideProjectRole[];
}

export function aggregateWorkspaceRoles(flat: IDomainWideProjectRole[] | null | undefined): IWorkspaceRole[] {
  const byName: Map<string, IWorkspaceRole> = new Map<string, IWorkspaceRole>();
  const ordered: IWorkspaceRole[] = [];

  for (const item of flat ?? []) {
    const trimmed: string = (item.name ?? "").trim();
    if (trimmed.length === 0) {
      continue;
    }
    // Fold key mirrors the seeding/authorization key (trimmed, case-insensitive). JS toLowerCase
    // is culture-independent, so this matches the C# OrdinalIgnoreCase fold.
    const key: string = trimmed.toLowerCase();
    let role: IWorkspaceRole | undefined = byName.get(key);
    if (role === undefined) {
      role = { name: trimmed, roleIds: [], projectCount: 0, occurrences: [] };
      byName.set(key, role);
      ordered.push(role);
    }
    role.occurrences.push(item);
    if (item.id !== null && !role.roleIds.includes(item.id)) {
      role.roleIds.push(item.id);
    }
  }

  for (const role of ordered) {
    const projectIds: Set<string> = new Set(
      role.occurrences
        .map((occurrence: IDomainWideProjectRole): string => occurrence.projectId)
        .filter((projectId: string): boolean => Boolean(projectId))
    );
    role.projectCount = projectIds.size;
  }

  return ordered;
}
